import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Heart, Sparkles } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';

/**
 * DonationThanksDialog — auto-opens on /support?donated=1 (the Stripe
 * Payment Link's after-payment redirect) and invites the donor to add
 * themselves to the Thank-You Wall while the moment is still warm.
 */
export const DonationThanksDialog = ({ open, onOpenChange, isSpanish }) => {
  const [draft, setDraft] = useState({ display_name: '', amount_usd: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async () => {
    const name = draft.display_name.trim();
    if (!name) {
      toast.error(isSpanish ? 'Tu nombre es obligatorio' : 'Display name is required');
      return;
    }
    setSubmitting(true);
    const body = { display_name: name };
    if (draft.amount_usd) body.amount_usd = Number(draft.amount_usd);
    if (draft.message.trim()) body.message = draft.message.trim();
    const { data, error } = await apiFetch('/api/support/thank-you-submit', {
      method: 'POST',
      body,
    });
    setSubmitting(false);
    if (error || !data?.ok) {
      toast.error(error?.detail || (isSpanish ? 'Error al enviar' : 'Submission failed'));
      return;
    }
    toast.success(
      isSpanish
        ? '¡Gracias! Tu nombre aparecerá en el muro tras una breve revisión.'
        : 'Thank you! Your name will appear on the wall after a quick review.'
    );
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md" data-testid="donation-thanks-dialog">
        <DialogHeader>
          <div className="text-5xl text-center mb-1" aria-hidden="true">💚🦕</div>
          <DialogTitle className="text-center text-xl">
            {isSpanish ? '¡Gracias por tu donación!' : 'Thank you for your donation!'}
          </DialogTitle>
          <DialogDescription className="text-center">
            {isSpanish
              ? 'Detective Dino recibió tu estrella de árbol. ¿Quieres aparecer en nuestro Muro de Agradecimiento?'
              : 'Detective Dino got your tree star! Want to be featured on our Thank-You Wall?'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input
            value={draft.display_name}
            onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
            placeholder={isSpanish ? 'Tu nombre (o apodo)' : 'Your name (or nickname)'}
            maxLength={60}
            data-testid="donation-thanks-name-input"
          />
          <Input
            type="number"
            min={0}
            step={1}
            value={draft.amount_usd}
            onChange={(e) => setDraft({ ...draft, amount_usd: e.target.value })}
            placeholder={isSpanish ? 'Monto USD (opcional)' : 'Amount USD (optional)'}
            data-testid="donation-thanks-amount-input"
          />
          <Textarea
            value={draft.message}
            onChange={(e) => setDraft({ ...draft, message: e.target.value })}
            placeholder={isSpanish ? 'Mensaje corto (opcional)' : 'Short message (optional)'}
            maxLength={240}
            rows={2}
            data-testid="donation-thanks-message-input"
          />
          <Button
            onClick={onSubmit}
            disabled={submitting}
            className="w-full bg-pink-500 hover:bg-pink-600 text-white"
            data-testid="donation-thanks-submit-btn"
          >
            <Heart className="h-4 w-4 mr-1.5" />
            {submitting
              ? (isSpanish ? 'Enviando…' : 'Submitting…')
              : (isSpanish ? 'Añádeme al muro' : 'Add me to the wall')}
          </Button>
          <Button
            variant="ghost"
            className="w-full text-muted-foreground"
            onClick={() => onOpenChange(false)}
            data-testid="donation-thanks-skip-btn"
          >
            {isSpanish ? 'Quizás luego' : 'Maybe later'}
          </Button>
          <p className="text-[10px] text-muted-foreground text-center flex items-center justify-center gap-1">
            <Sparkles className="h-3 w-3 text-amber-500" />
            {isSpanish
              ? 'Las entradas son moderadas antes de aparecer.'
              : 'Entries are moderated before they appear.'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DonationThanksDialog;
